import { useRef, useMemo, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import initExporting from "highcharts/modules/exporting";
import initOfflineExporting from "highcharts/modules/offline-exporting";
import HighchartsReact from "highcharts-react-official";
import { Empty, Typography } from "antd";

import type { RadarComponentSpec } from "../Processing/types";
import { useSlicerVariables, useWidgetData } from "../Contexts/dashboardHooks";
import { useChartSync } from "../Contexts/ChartSyncContext";
import { WidgetInfoButton } from "../Info/WidgetInfoButton";
import { resolveTemplate } from "../Utils/resolveTemplate";

HighchartsMore(Highcharts);
initExporting(Highcharts);
initOfflineExporting(Highcharts);

const CHART_CONTAINER_STYLE: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
};

const { Text } = Typography;

const DEFAULT_TITLE: Record<string, string> = {
  metric:   "Radar Chart",
  player:   "Player Radar",
  position: "Position Radar",
  drill:    "Drill Radar",
};

interface Props {
  id: string;
}

export function RadarWidget({ id }: Props) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const data = useWidgetData(id);
  const slicerValues = useSlicerVariables();
  const syncContext = useChartSync();

  useEffect(() => {
    if (!syncContext) return;
    const getCell = () =>
      containerRef.current?.closest(".grid-item-wrapper") ?? containerRef.current ?? null;
    const getChart = () => chartRef.current?.chart ?? null;
    syncContext.register(id, getCell, getChart);
    const t = setTimeout(() => syncContext.requestSync(), 200);
    return () => {
      clearTimeout(t);
      syncContext.unregister(id);
    };
  }, [id, syncContext]);

  const spec = data?.type === "radar" ? (data.spec as RadarComponentSpec) : null;
  const rawOptions = data?.type === "radar" ? data.radar.chartOptions : null;

  const options = useMemo(() => {
    if (!rawOptions) return null;
    return {
      ...rawOptions,
      chart: {
        ...((rawOptions.chart as Record<string, unknown>) ?? {}),
        reflow: true,
      },
      exporting: {
        buttons: {
          contextButton: { align: "right", verticalAlign: "top", x: 6, y: -4 },
        },
      },
    };
  }, [rawOptions]);

  if (!options || !spec) {
    return (
      <div className="chart-widget" data-grid-id={id} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Empty description="No data found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  const vertexDimension =
    spec.vertexDimension ?? (spec.radar_builder_misc?.vertexDimension as string | undefined) ?? "metric";

  const hasData =
    Array.isArray((options as any)?.series) &&
    (options as any).series.some(
      (s: any) => Array.isArray(s.data) && s.data.some((d: any) => d !== null),
    );

  const title =
    resolveTemplate(spec.display.title ?? "", slicerValues).resolved || DEFAULT_TITLE[vertexDimension] || "Radar Chart";

  return (
    <div ref={containerRef} className="chart-widget" data-grid-id={id}>
      <div className="widget-header">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Text strong style={{ fontSize: 18 }}>{title}</Text>
          <WidgetInfoButton id={id} />
        </span>
      </div>
      <div className="chart-widget-body" style={{ position: "relative" }}>
        <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: CHART_CONTAINER_STYLE }}
        />
        {!hasData && (
          <div style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}>
            <Empty description="No data found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        )}
      </div>
    </div>
  );
}
